import React, { useState, useEffect } from "react";

// Helper: shuffle an array (Fisher-Yates)
function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const PageNotFound = () => {
  // -- STATES FOR LOADED WORD LISTS --
  const [dictionary, setDictionary] = useState([]);
  const [sixLetterWords, setSixLetterWords] = useState([]);

  // -- GAME STATES --
  // Each puzzle tile: { id, letter, used }
  const [puzzleTiles, setPuzzleTiles] = useState([]);
  // guessOrder: array of tile ids (from puzzleTiles) in order of use.
  const [guessOrder, setGuessOrder] = useState([]);
  const [wordBank, setWordBank] = useState([]); // All valid words for puzzle.
  const [foundWords, setFoundWords] = useState([]);
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState({ text: "", color: "" });
  const [timeLeft, setTimeLeft] = useState(60);
  const [singleDigitTime, setSingleDigitTimeLeft] = useState(false);
  const [preventKeyDown, setPreventKeyDown] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  // gameOver becomes true when time runs out.
  const [gameOver, setGameOver] = useState(false);
  // During game over, wait 2 seconds then show the results page.
  const [showResults, setShowResults] = useState(false);
  // Controls flash effect on puzzle row.
  const [puzzleFlash, setPuzzleFlash] = useState("");

  // --------------------- FETCH WORD LISTS ---------------------
  useEffect(() => {
    fetch("/assets/.lists/lessthanorequalto6-collins-words-2019.txt")
      .then((res) => res.text())
      .then((text) => {
        const allWords = text
          .split("\n")
          .map((w) => w.trim().toLowerCase())
          .filter(Boolean);
        setDictionary(allWords);
      })
      .catch((err) => console.error("Error fetching words.txt:", err));
  }, []);

  useEffect(() => {
    fetch("/assets/.lists/equalto6-collins-words-2019.txt")
      .then((res) => res.text())
      .then((text) => {
        const valid6Words = text
          .split("\n")
          .map((w) => w.replace(/[^a-zA-Z]/g, "").toLowerCase().trim())
          .filter((w) => w.length === 6);
        setSixLetterWords(valid6Words);
      })
      .catch((err) => console.error("Error fetching 6letterwords.txt:", err));
  }, []);

  // --------------------- TIMER & TRANSITION ---------------------
  // End game when timer runs out.
  useEffect(() => {
    if (gameStarted && !gameOver && timeLeft <= 0) {
      setGameOver(true);
    }
  }, [timeLeft, gameOver, gameStarted]);

  // Countdown timer.
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const timerId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [gameStarted, gameOver]);

  // Single digit timer formatting.
  useEffect(() => {
    if (gameStarted && !gameOver && timeLeft < 10) {
      setSingleDigitTimeLeft(true);
    } else {
      setSingleDigitTimeLeft(false);
    }
  }, [timeLeft, gameOver, gameStarted]);

  // When gameOver becomes true, wait 2 seconds then show result page.
  useEffect(() => {
    if (gameOver) {
      const timer = setTimeout(() => {
        setShowResults(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowResults(false);
    }
  }, [gameOver]);

  // --------------------- TILE MANIPULATION ---------------------
  // Add a letter. When typing, tile id is null so first available tile is chosen.
  // When clicking, pass the tile id.
  const addLetter = (letter, id = null) => {
    if (id !== null) {
      // Use the specific tile clicked.
      const clickedTile = puzzleTiles.find(
        (tile) => tile.letter === letter && tile.id === id && !tile.used
      );
      if (clickedTile) {
        const newTiles = puzzleTiles.map((tile) =>
          tile.id === clickedTile.id ? { ...tile, used: true } : tile
        );
        setPuzzleTiles(newTiles);
        setGuessOrder([...guessOrder, clickedTile.id]);
      }
    } else {
      // Typing: pick the first available tile with the letter.
      const availableTile = puzzleTiles.find(
        (tile) => tile.letter === letter && !tile.used
      );
      if (availableTile) {
        const newTiles = puzzleTiles.map((tile) =>
          tile.id === availableTile.id ? { ...tile, used: true } : tile
        );
        setPuzzleTiles(newTiles);
        setGuessOrder([...guessOrder, availableTile.id]);
      }
    }
  };

  // Remove the last letter (for backspace).
  const removeLastLetter = () => {
    if (guessOrder.length === 0) return;
    const lastId = guessOrder[guessOrder.length - 1];
    setGuessOrder(guessOrder.slice(0, -1));
    const newTiles = puzzleTiles.map((tile) =>
      tile.id === lastId ? { ...tile, used: false } : tile
    );
    setPuzzleTiles(newTiles);
  };

  // Remove a specific letter from guess (when clicking a guess tile).
  const removeLetterFromGuess = (tileId, index) => {
    const newGuessOrder = [...guessOrder];
    newGuessOrder.splice(index, 1);
    setGuessOrder(newGuessOrder);
    const newTiles = puzzleTiles.map((tile) =>
      tile.id === tileId ? { ...tile, used: false } : tile
    );
    setPuzzleTiles(newTiles);
  };

  // --------------------- GLOBAL KEY LISTENER ---------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!gameStarted || preventKeyDown) return;
      if (gameOver) return; // Prevent input when game is over.
      if (/^[a-zA-Z]$/.test(e.key)) {
        addLetter(e.key.toLowerCase(), null);
      } else if (e.key === "Backspace") {
        removeLastLetter();
      } else if (e.key === "Enter") {
        submitGuess();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameStarted, gameOver, puzzleTiles, guessOrder]);

  // --------------------- GAME CONTROL ---------------------
  // Start a new game.
  const startGame = () => {
    // Reset result view if needed.
    setShowResults(false);
    if (!dictionary.length || !sixLetterWords.length) {
      console.warn("Cannot start game without word lists loaded.");
      return;
    }
    const randomIndex = Math.floor(Math.random() * sixLetterWords.length);
    const chosenPuzzle = sixLetterWords[randomIndex];
    // For a twist, leave the last letter in place; shuffle the first five.
    const firstFive = chosenPuzzle.slice(0, 5);
    const lastLetter = chosenPuzzle.slice(-1);
    const shuffledLetters = shuffleArray(firstFive.split(""));
    shuffledLetters.push(lastLetter);
    const tiles = shuffledLetters.map((letter, index) => ({
      id: index,
      letter,
      used: false,
    }));
    const possibleWords = dictionary.filter((w) => canFormWord(w, chosenPuzzle));
    setPuzzleTiles(tiles);
    setGuessOrder([]);
    setWordBank(possibleWords);
    setFoundWords([]);
    setScore(0);
    setMessage({ text: "", color: "" });
    setTimeLeft(60);
    setGameStarted(true);
    setGameOver(false);
  };

  // Check if a word can be formed from the puzzle's letters.
  const canFormWord = (word, puzzle) => {
    if (word.length < 3) return false;
    if (word.length > puzzle.length) return false;
    const puzzleCount = {};
    for (const c of puzzle) {
      puzzleCount[c] = (puzzleCount[c] || 0) + 1;
    }
    for (const c of word) {
      if (!puzzleCount[c]) return false;
      puzzleCount[c]--;
      if (puzzleCount[c] < 0) return false;
    }
    return true;
  };

  // Submit the current guess.
  const submitGuess = () => {
    if (gameOver) {
      // If game is over and results are showing, allow restart.
      if (showResults) startGame();
      return;
    }
    const guessStr = guessOrder
      .map((id) => {
        const tile = puzzleTiles.find((t) => t.id === id);
        return tile ? tile.letter : "";
      })
      .join("")
      .toLowerCase();
    if (guessStr.length < 3) return;
    if (!wordBank.includes(guessStr)) {
      setMessage({ text: "Not in vocabulary", color: "red" });
      flashPuzzleTiles("red");
      return;
    }
    if (foundWords.includes(guessStr)) {
      setMessage({ text: "Already used", color: "yellow" });
      flashPuzzleTiles("yellow");
      return;
    }
    setFoundWords([...foundWords, guessStr]);
    let points = 0;
    switch (guessStr.length) {
      case 3:
        points = 100;
        break;
      case 4:
        points = 400;
        break;
      case 5:
        points = 1200;
        break;
      case 6:
        points = 2000;
        break;
      default:
        points = 0;
    }
    const messageStr = `${guessStr} (+${points})`;
    setMessage({ text: messageStr, color: "green" });
    setScore(score + points);
    // Reset: mark all used tiles as available.
    const resetTiles = puzzleTiles.map((tile) => ({ ...tile, used: false }));
    setPuzzleTiles(resetTiles);
    setGuessOrder([]);
  };

  // Flash the available (blue) puzzle tiles by setting puzzleFlash.
  const flashPuzzleTiles = (color) => {
    setPuzzleFlash(color);
    // setPreventKeyDown(true);
    // Reset used tiles.
    const resetTiles = puzzleTiles.map((tile) =>
      guessOrder.includes(tile.id) ? { ...tile, used: false } : tile
    );
    setPuzzleTiles(resetTiles);
    setGuessOrder([]);
    setTimeout(() => {
      setPuzzleFlash("");
      setPreventKeyDown(false);
    }, 250);
  };

  // Shuffle the puzzle row: reshuffle tile order and clear guess.
  const handleShuffle = () => {
    const resetTiles = puzzleTiles.map((tile) => ({ ...tile, used: false }));
    const shuffled = shuffleArray(resetTiles);
    setPuzzleTiles(shuffled);
    setGuessOrder([]);
  };

  // --------------------- STYLING ---------------------
  // Each tile should be proportional: 7 tiles fill the width.
  const tileClass =
    "w-[14.2857vw] h-[14.2857vw] md:w-20 md:h-20 flex items-center justify-center text-xl sm:text-2xl md:text-4xl font-bold rounded";

  // --------------------- RESULT PAGE ---------------------
  // Compute sorted result list: sort wordBank by length then alphabetically.
  const sortedResults = [...wordBank].sort((a, b) => {
    if (a.length !== b.length) return b.length - a.length;
    return a.localeCompare(b);
  });

  // --------------------- RENDER ---------------------
  return (
    <div className="min-h-screen">
      <main className="min-h-screen bg-cover bg-center bg-fixed flex flex-col items-center justify-center">
        {/* 404 Header */}
        <div className="flex flex-col items-center mx-auto text-black font-semibold mt-20 mb-2">
          <h1 className="md:text-8xl text-4xl">404</h1>
          <h2 className="md:text-2xl text-xl">
            Page not found.{" "}
            <a href="/" className="text-cyan-600 hover:text-cyan-500 underline">
              Homepage?
            </a>
          </h2>
        </div>

        {/* ANAGRAMS GAME or RESULT PAGE */}
        <div
          className="flex flex-col items-center md:w-[75%] w-full text-black mb-4 p-4"
          style={{ background: "none" }}
        >
          {!gameStarted ? (
            <button
              onClick={startGame}
              className="transition-all duration-300 ease-in-out px-6 py-3 bg-cyan-600 text-white font-bold rounded hover:bg-cyan-700"
            >
              Anagrams
            </button>
          ) : showResults ? (
            // RESULT PAGE: Grid of all possible words.
            <div className="w-full">
              <h3 className="text-lg font-semibold mb-2 text-center">All Possible Words ({foundWords.length}/{wordBank.length})</h3>
              <div className="text-center grid grid-cols-6 gap-2 md:grid-cols-8">
                {sortedResults.map((word, index) => {
                  const guessed = foundWords.includes(word);
                  return (
                    <a
                      key={index}
                      href={`https://www.google.com/search?q=define+${word}`}
                      target="_blank"
                      rel="noreferrer"
                      className={`text-xs md:text-sm hover:underline ${
                        guessed ? "font-bold text-cyan-600" : "text-gray-700"
                      }`}
                    >
                      {word}
                    </a>
                  );
                })}
              </div>
              {/* Play Again Button */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={startGame}
                  className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Play Again
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Timer / Score */}
              {!gameOver ? (
                <p className="md:text-2xl text-xl font-semibold mb-2">
                  00:{!singleDigitTime ? `${timeLeft}` : `0${timeLeft}`}
                </p>
              ) : (
                <p className="md:text-2xl text-xl font-semibold mb-2 text-red-500">
                  00:00
                </p>
              )}
              <p className="text-2xl font-bold text-cyan-600 mb-2">{score}</p>

              {/* Guess Row */}
              <div className="flex space-x-2 mb-2 flex-nowrap overflow-x-auto justify-center select-none">
                {Array.from({ length: 6 }).map((_, i) => {
                  if (i < guessOrder.length) {
                    const tileId = guessOrder[i];
                    const tile = puzzleTiles.find((t) => t.id === tileId);
                    return (
                      <button
                        key={i}
                        onClick={() => removeLetterFromGuess(tileId, i)}
                        className={`${tileClass} bg-cyan-600 text-white`}
                      >
                        {tile ? tile.letter.toUpperCase() : ""}
                      </button>
                    );
                  } else {
                    return (
                      <div
                        key={i}
                        className={`${tileClass} bg-gray-300 text-transparent`}
                      >
                        X
                      </div>
                    );
                  }
                })}
              </div>

              {/* Message */}
              {message.text ? (
                !gameOver ? (
                  <p
                    className={
                      message.color === "green"
                        ? "md:text-xl text-lg font-semibold text-green-600"
                        : message.color === "yellow"
                        ? "md:text-xl text-lg font-semibold text-yellow-600"
                        : "md:text-xl text-lg font-semibold text-red-600"
                    }
                  >
                    {message.text}
                  </p>
                ) : (
                  <p className="text-xl font-semibold text-gray-600">...</p>
                )
              ) : (
                <p className="text-xl font-semibold text-gray-600">...</p>
              )}

              {/* Puzzle Row */}
              <div className="flex space-x-2 mt-2 mb-4 flex-nowrap overflow-x-auto justify-center select-none">
                {puzzleTiles.map((tile) => {
                  let bgClass = "";
                  if (puzzleFlash === "red") {
                    bgClass = tile.used
                      ? "bg-red-600 text-transparent"
                      : "bg-red-600 text-white";
                  } else if (puzzleFlash === "yellow") {
                    bgClass = tile.used
                      ? "bg-yellow-600 text-transparent"
                      : "bg-yellow-600 text-white";
                  } else if (tile.used) {
                    bgClass = "bg-gray-300 text-transparent cursor-default";
                  } else {
                    bgClass = "bg-cyan-600 text-white hover:bg-cyan-700";
                  }
                  return (
                    <button
                      key={tile.id}
                      onClick={() => {
                        if (!tile.used) addLetter(tile.letter, tile.id);
                      }}
                      className={`${tileClass} ${bgClass}`}
                    >
                      {tile.used ? "X" : tile.letter.toUpperCase()}
                    </button>
                  );
                })}
              </div>

              {/* Control Buttons */}
              <div className="flex space-x-4 mb-2">
                {/* <button
                  onClick={handleShuffle}
                  className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Shuffle
                </button> */}
                <button
                  onClick={submitGuess}
                  className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-800"
                >
                  {gameOver ? "Play Again" : "Submit"}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default PageNotFound;
